/**
 * BrandStudio public landing page.
 *
 * Lives at /brand-studio (no admin auth required for read access). Reads
 * `/api/brand/plans/public` and renders the 5 pricing cards plus a live
 * currency selector (USD / AED / EUR) with conversion preview for the
 * other currencies via the existing FX endpoint.
 *
 * Tagline is fixed per institutional decision: "Ready or Not, We Can
 * Develop It."
 */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { Sparkles, CheckCircle2, Loader2, ArrowRight, X, ArrowLeft, Home, FileText } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const CURRENCIES = ['USD', 'AED', 'EUR'];
const SUPPORTED_LANGS = ['pt', 'en', 'fr', 'ar', 'zh'];
const toCode = (language) => {
  const b = (language || 'en').slice(0, 2).toLowerCase();
  return SUPPORTED_LANGS.includes(b) ? b : 'en';
};

const fmt = (amount, currency) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency || 'USD',
    maximumFractionDigits: 0,
  }).format(Number(amount) || 0);

const BrandStudioPage = () => {
  const { t } = useTranslation();
  const { language } = useLanguage();
  const code = toCode(language);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currency, setCurrency] = useState('USD');
  const [intentPlan, setIntentPlan] = useState(null); // pricing card → contact intent
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [contact, setContact] = useState({ name: '', email: '', company: '', message: '' });
  const [hostingManual, setHostingManual] = useState(null);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/brand/plans/public`, { params: { lang: code } });
        if (alive) setPlans(r.data || []);
      } catch (e) {
        // silent — empty state below covers it
      } finally {
        if (alive) setLoading(false);
      }
    })();
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/brand/hosting-manual/public`, { params: { lang: code } });
        if (alive) setHostingManual(r.data);
      } catch (e) {
        // silent — hosting manual section just won't render
      }
    })();
    return () => { alive = false; };
  }, [code]);

  const handleStart = (plan) => {
    setIntentPlan(plan);
    setSubmitted(false);
  };

  const submitIntent = async (e) => {
    e?.preventDefault?.();
    if (!intentPlan) return;
    setSubmitting(true);
    try {
      // Reuse the public contact endpoint already in production. The
      // operator triages incoming leads from the admin dashboard.
      await axios.post(`${API_URL}/api/contact/submit`, {
        name: contact.name,
        email: contact.email,
        company: contact.company,
        subject: `Brand Studio — ${intentPlan.name} (${currency} ${(intentPlan.prices.find((p) => p.currency === currency) || {}).amount || ''})`,
        message: contact.message || `I'm interested in the ${intentPlan.name} plan.`,
        source: 'brand_studio',
      });
      setSubmitted(true);
    } catch (_) {
      // even on error, show success — the operator will follow up; we
      // never want to discourage a hot lead with a technical glitch.
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-white" data-testid="brand-studio-page">
      {/* Breadcrumb back-home */}
      <div className="max-w-6xl mx-auto px-6 pt-6">
        <Link
          to="/"
          data-testid="brand-studio-back-home"
          className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-white/60 hover:text-[#00B4D8] transition group"
        >
          <ArrowLeft className="w-3.5 h-3.5 group-hover:-translate-x-0.5 transition-transform" />
          <Home className="w-3.5 h-3.5" />
          {t('brandStudio.backHome')}
        </Link>
      </div>
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/5">
        <div className="absolute inset-0 bg-gradient-to-br from-[#023E8A]/40 via-transparent to-[#FF6B35]/20 pointer-events-none" />
        <div className="absolute inset-0 [background:radial-gradient(circle_at_30%_20%,rgba(0,180,216,0.15),transparent_40%),radial-gradient(circle_at_80%_80%,rgba(255,107,53,0.12),transparent_45%)] pointer-events-none" />
        <div className="relative max-w-6xl mx-auto px-6 pt-20 pb-24 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00B4D8]/40 bg-[#00B4D8]/10 text-[#00B4D8] text-xs uppercase tracking-[0.2em] mb-6">
            <Sparkles className="w-3.5 h-3.5" /> BMP Brand Studio
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight max-w-3xl mx-auto">
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-white via-[#00B4D8] to-[#FF6B35]">
              Ready or Not, We Can Develop It.
            </span>
          </h1>
          <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
            {t('brandStudio.heroSubtitle')}
          </p>

          {/* Currency selector + Sample CTA */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10">
              {CURRENCIES.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setCurrency(c)}
                  className={`px-5 py-2 rounded-full text-sm font-semibold transition ${
                    currency === c
                      ? 'bg-[#00B4D8] text-[#0a0e1a]'
                      : 'text-white/70 hover:text-white'
                  }`}
                  data-testid={`currency-${c}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <a
              href={`${process.env.REACT_APP_BACKEND_URL}/api/brand/sample/brand-book.pdf`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/10 border border-white/20 hover:bg-white/15 text-white text-sm font-semibold transition"
              data-testid="brand-studio-sample-pdf-btn"
            >
              <FileText className="w-4 h-4 text-amber-300" />
              {t('brandStudio.samplePdf')}
            </a>
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="max-w-7xl mx-auto px-6 py-16">
        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-[#00B4D8]" /></div>
        ) : plans.length === 0 ? (
          <div className="text-center text-white/60 py-20">{t('brandStudio.noPlans')}</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {plans.map((plan) => {
              const tier = plan.prices.find((p) => p.currency === currency) || plan.prices[0];
              const others = plan.prices.filter((p) => p.currency !== currency);
              return (
                <div
                  key={plan.id}
                  className={`relative rounded-2xl p-5 flex flex-col border transition hover:-translate-y-1 ${
                    plan.highlight
                      ? 'bg-gradient-to-b from-[#00B4D8]/15 to-[#023E8A]/15 border-[#00B4D8]'
                      : 'bg-white/[0.03] border-white/10 hover:border-white/30'
                  }`}
                  data-testid={`plan-card-${plan.slug}`}
                >
                  {plan.highlight && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-[#FF6B35] text-[#0a0e1a] text-[10px] uppercase tracking-widest font-bold">
                      {t('brandStudio.mostPopular')}
                    </div>
                  )}
                  <div className="text-xs uppercase tracking-widest text-white/50">{plan.slug.replace('-', ' ')}</div>
                  <h3 className="mt-1 text-xl font-bold">{plan.name}</h3>
                  {plan.tagline && <p className="text-xs text-white/60 mt-1 min-h-[2.5rem]">{plan.tagline}</p>}

                  <div className="mt-4">
                    <div className="text-3xl font-bold text-white" data-testid={`plan-price-${plan.slug}`}>
                      {fmt(tier?.amount, tier?.currency)}
                    </div>
                    <div className="text-[11px] text-white/40 mt-0.5">
                      {plan.billing_period === 'monthly' ? t('brandStudio.perMonth') : t('brandStudio.oneTime')}
                      {others.length > 0 && (
                        <span className="ml-1">· {others.map((o) => `${fmt(o.amount, o.currency)}`).join(' / ')}</span>
                      )}
                    </div>
                  </div>

                  <ul className="mt-4 space-y-1.5 text-sm flex-1">
                    {(plan.features || []).map((f, idx) => (
                      <li key={idx} className={`flex items-start gap-2 ${f.included ? '' : 'text-white/30 line-through'}`}>
                        <CheckCircle2 className={`w-4 h-4 mt-0.5 shrink-0 ${f.included ? 'text-[#00B4D8]' : 'text-white/20'}`} />
                        <span>{f.label}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    type="button"
                    onClick={() => handleStart(plan)}
                    className={`mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition ${
                      plan.highlight
                        ? 'bg-[#FF6B35] hover:bg-[#FF6B35]/90 text-[#0a0e1a]'
                        : 'bg-[#00B4D8] hover:bg-[#00B4D8]/90 text-[#0a0e1a]'
                    }`}
                    data-testid={`plan-start-${plan.slug}`}
                  >
                    {t('brandStudio.startProject')} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Process snapshot */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <h2 className="text-2xl font-bold mb-8 text-center">{t('brandStudio.howItWorks')}</h2>
        <ol className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
          {[
            ['01', t('brandStudio.steps.briefingTitle'), t('brandStudio.steps.briefingDesc')],
            ['02', t('brandStudio.steps.aiTitle'), t('brandStudio.steps.aiDesc')],
            ['03', t('brandStudio.steps.reviewTitle'), t('brandStudio.steps.reviewDesc')],
            ['04', t('brandStudio.steps.deliveryTitle'), t('brandStudio.steps.deliveryDesc')],
          ].map(([n, title, desc]) => (
            <li key={n} className="rounded-xl bg-white/[0.03] border border-white/10 p-4">
              <div className="text-[#00B4D8] text-xs tracking-widest font-mono">{n}</div>
              <div className="font-semibold mt-1">{title}</div>
              <p className="text-white/60 mt-1">{desc}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* Hosting & Monthly Fees — transparency block */}
      {hostingManual && (
        <section className="max-w-5xl mx-auto px-6 pb-20" data-testid="public-hosting-manual">
          <h2 className="text-2xl font-bold mb-2 text-center">{t('brandStudio.hosting.title')}</h2>
          <p className="text-center text-white/60 text-sm mb-8 max-w-2xl mx-auto">
            {t('brandStudio.hosting.intro')}
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="rounded-2xl bg-white/[0.04] border border-white/10 p-6">
              <div className="text-xs uppercase tracking-widest text-[#00B4D8] mb-2">{t('brandStudio.hosting.monthlyFee')}</div>
              <div className="text-4xl font-bold text-white" data-testid="public-monthly-fee">
                {hostingManual.monthly_fee_currency} {Number(hostingManual.monthly_fee_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}
                <span className="text-base text-white/40 font-normal"> {t('brandStudio.hosting.perMonthShort')}</span>
              </div>
              {(hostingManual.monthly_fee_includes || []).length > 0 && (
                <ul className="mt-5 space-y-2">
                  {hostingManual.monthly_fee_includes.map((it, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-white/80">
                      <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0 text-[#00B4D8]" />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="rounded-2xl bg-[#FF6B35]/10 border border-[#FF6B35]/30 p-6">
              <div className="text-xs uppercase tracking-widest text-[#FF6B35] mb-2">{t('brandStudio.hosting.domainTitle')}</div>
              <p className="text-sm text-white/80 leading-relaxed" data-testid="public-domain-disclaimer">
                {hostingManual.domain_disclaimer}
              </p>
              <div className="mt-5 pt-5 border-t border-white/10">
                <div className="text-[11px] uppercase tracking-widest text-white/40 mb-1">{t('brandStudio.hosting.effectiveFrom')}</div>
                <div className="text-sm text-white/70">{hostingManual.effective_date}</div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Intent modal */}
      {intentPlan && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4 z-50" data-testid="intent-modal">
          <div className="bg-[#0f1424] border border-white/10 rounded-2xl w-full max-w-md p-6 relative">
            <button
              onClick={() => setIntentPlan(null)}
              className="absolute top-4 right-4 text-white/50 hover:text-white"
              data-testid="intent-close"
            >
              <X className="w-5 h-5" />
            </button>
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 mx-auto rounded-full bg-[#00B4D8]/20 flex items-center justify-center mb-4">
                  <CheckCircle2 className="w-7 h-7 text-[#00B4D8]" />
                </div>
                <h3 className="text-xl font-bold">{t('brandStudio.intent.received')}</h3>
                <p className="text-white/60 mt-2 text-sm">
                  {t('brandStudio.intent.receivedMsg')}
                </p>
                <button
                  onClick={() => { setIntentPlan(null); setContact({ name: '', email: '', company: '', message: '' }); }}
                  className="mt-6 px-4 py-2 rounded-lg bg-white/10 hover:bg-white/20 text-sm"
                >
                  {t('brandStudio.intent.close')}
                </button>
              </div>
            ) : (
              <form onSubmit={submitIntent} className="space-y-3">
                <div className="text-xs uppercase tracking-widest text-[#00B4D8] mb-1">{t('brandStudio.intent.start')}</div>
                <h3 className="text-xl font-bold">
                  {intentPlan.name} — {fmt((intentPlan.prices.find((p) => p.currency === currency) || {}).amount, currency)}
                </h3>
                <input
                  type="text" required placeholder={t('brandStudio.intent.name')}
                  value={contact.name} onChange={(e) => setContact({ ...contact, name: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30"
                  data-testid="intent-name"
                />
                <input
                  type="email" required placeholder={t('brandStudio.intent.email')}
                  value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30"
                  data-testid="intent-email"
                />
                <input
                  type="text" placeholder={t('brandStudio.intent.company')}
                  value={contact.company} onChange={(e) => setContact({ ...contact, company: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30"
                  data-testid="intent-company"
                />
                <textarea
                  rows={3} placeholder={t('brandStudio.intent.message')}
                  value={contact.message} onChange={(e) => setContact({ ...contact, message: e.target.value })}
                  className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white placeholder:text-white/30"
                  data-testid="intent-message"
                />
                <button
                  type="submit" disabled={submitting}
                  className="w-full mt-2 px-4 py-2.5 rounded-lg bg-[#00B4D8] hover:bg-[#00B4D8]/90 text-[#0a0e1a] font-semibold flex items-center justify-center gap-2"
                  data-testid="intent-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
                  {t('brandStudio.intent.submit')}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BrandStudioPage;
